import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Phone,
} from "lucide-react";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import SEO from "../components/seo/SEO";
import { useLanguage } from "../i18n/LanguageContext";
import {
  SEO_DATA,
  SERVICE_CONTENT,
  SERVICE_KEYS,
  PAGE_COPY,
  pathFor,
} from "../i18n/seoMeta";

// Hero/illustration image per service detail page. Shown below the
// "What you actually get" sidebar, outside the dark card.
const SERVICE_IMAGES = {
  "service.mentoring":
    "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1200&q=80",
  "service.consulting":
    "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=80",
  "service.softSkills":
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=80",
  "service.agile":
    "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
  "service.okr":
    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80",
  "service.change":
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1200&q=80",
  "service.teamDynamics":
    "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1200&q=80",
  "service.collaboration":
    "https://images.pexels.com/photos/5257576/pexels-photo-5257576.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

const CONTACT_PHONE_DISPLAY = "+420 602 461 799";
const CONTACT_PHONE_TEL = "+420602461799";

// Generic detail page for a single service. The `serviceId` prop (e.g.
// "service.mentoring") drives both the SEO metadata and the body content.
// Same layout is reused across CZ and EN routes.
export const ServiceDetailPage = ({ serviceId }) => {
  const { lang } = useLanguage();
  const meta = SEO_DATA[lang][serviceId];
  const content = SERVICE_CONTENT[lang][serviceId];
  const copy = PAGE_COPY[lang].serviceDetail;
  const homePath = pathFor("home", lang);
  const servicesPath = pathFor("services", lang);

  // 2 "Other services" picked deterministically as the next two in the canonical
  // service order, wrapping around.
  const otherIds = (() => {
    const idx = SERVICE_KEYS.indexOf(serviceId);
    return [
      SERVICE_KEYS[(idx + 1) % SERVICE_KEYS.length],
      SERVICE_KEYS[(idx + 2) % SERVICE_KEYS.length],
    ];
  })();

  return (
    <div data-testid={`service-detail-${serviceId}`} className="bg-[#181B24] text-white">
      <SEO routeId={serviceId} lang={lang} />
      <Header />
      <main>
        {/* Header band */}
        <section className="bg-[#202535] pt-36 md:pt-44 pb-16 md:pb-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
            <nav
              data-testid="service-breadcrumb"
              className="font-body text-sm text-white/60 mb-6 flex items-center gap-2 flex-wrap"
            >
              <Link to={homePath} className="hover:text-[#FFE21A] transition-colors">
                {lang === "cs" ? "Domů" : "Home"}
              </Link>
              <ChevronRight size={14} />
              <Link to={servicesPath} className="hover:text-[#FFE21A] transition-colors">
                {SEO_DATA[lang].services.h1}
              </Link>
              <ChevronRight size={14} />
              <span className="text-white/90">{meta.h1}</span>
            </nav>

            <p className="font-body italic text-[#FFE21A]/90 text-sm md:text-base mb-4 tracking-wide">
              {content.eyebrow}
            </p>
            <h1
              data-testid="service-h1"
              className="font-heading uppercase font-bold leading-[1.05] tracking-[0.01em] text-4xl sm:text-5xl md:text-6xl"
            >
              {meta.h1}
            </h1>
            <p
              data-testid="service-lead"
              className="mt-6 max-w-3xl font-body text-white/85 text-base md:text-lg leading-relaxed"
            >
              {content.lead}
            </p>
          </div>
        </section>

        {/* Body sections + bullets */}
        <section className="bg-[#F3F4F5] text-[#181B24] py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-7 space-y-10">
                {content.sections.map((sec) => (
                  <article
                    key={sec.title}
                    data-testid={`service-section-${sec.title.toLowerCase().replace(/\s+/g, "-").slice(0, 40)}`}
                  >
                    <h2 className="font-heading font-bold uppercase tracking-[0.02em] text-2xl md:text-3xl text-[#181B24] mb-3 leading-tight">
                      {sec.title}
                    </h2>
                    <p className="font-body text-[#181B24]/85 text-base md:text-lg leading-relaxed">
                      {sec.body}
                    </p>
                  </article>
                ))}
              </div>

              <aside
                data-testid="service-bullets"
                className="lg:col-span-5"
              >
                <div className="sticky top-28 space-y-6">
                  <div className="rounded-2xl bg-[#181B24] text-white p-7 md:p-8 shadow-[0_25px_60px_rgba(24,27,36,0.18)]">
                    <h3 className="font-heading font-bold uppercase tracking-[0.02em] text-xl mb-5">
                      {copy.bulletsTitle}
                    </h3>
                    <ul className="space-y-3 font-body text-base">
                      {content.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3">
                          <CheckCircle2 size={18} className="text-[#FFE21A] mt-0.5 flex-shrink-0" />
                          <span className="text-white/90 leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>

                    <a
                      href={`tel:${CONTACT_PHONE_TEL}`}
                      data-testid="service-side-cta"
                      className="mt-7 inline-flex items-center justify-between gap-3 w-full rounded-full bg-[#FFE21A] text-[#181B24] font-heading font-bold uppercase tracking-[0.15em] text-sm px-5 py-4 hover-lift"
                    >
                      <span className="flex items-center gap-2">
                        <Phone size={16} strokeWidth={2.5} />
                        {CONTACT_PHONE_DISPLAY}
                      </span>
                      <ArrowRight size={16} />
                    </a>
                  </div>

                  {/* Service-specific illustration image, lives below the dark
                      "What you actually get" card on every detail page. */}
                  {SERVICE_IMAGES[serviceId] && (
                    <div
                      data-testid="service-image"
                      className="rounded-2xl overflow-hidden shadow-[0_15px_40px_rgba(24,27,36,0.12)] aspect-[4/3]"
                    >
                      <img
                        src={SERVICE_IMAGES[serviceId]}
                        alt={meta.h1}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              </aside>
            </div>

            <div className="mt-12">
              <Link
                to={homePath}
                data-testid="service-back-link"
                className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.15em] text-sm text-[#181B24] hover:text-[#FFE21A] transition-colors"
              >
                <ArrowLeft size={14} />
                {copy.backToServices}
              </Link>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-[#FFE21A] text-[#181B24] py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
            <h2 className="font-heading uppercase font-bold tracking-[0.01em] text-3xl md:text-5xl leading-[1.05] mb-4">
              {copy.ctaTitle}
            </h2>
            <p className="font-body text-[#181B24]/85 text-base md:text-lg max-w-2xl mx-auto mb-8">
              {copy.ctaText}
            </p>
            <Link
              to={`${homePath}#contact`}
              data-testid="service-bottom-cta"
              className="inline-flex items-center gap-3 rounded-full bg-[#181B24] text-[#FFE21A] font-heading font-bold uppercase tracking-[0.18em] text-sm md:text-base px-8 py-5 hover-lift shadow-[0_15px_40px_rgba(24,27,36,0.25)]"
            >
              {copy.ctaButton}
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        {/* Related services */}
        <section
          data-testid="related-services"
          className="bg-[#F3F4F5] text-[#181B24] py-16 md:py-20"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
            <h2 className="font-heading uppercase font-bold tracking-[0.01em] text-2xl md:text-3xl mb-8">
              {copy.relatedTitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {otherIds.map((id) => {
                const sm = SEO_DATA[lang][id];
                return (
                  <Link
                    key={id}
                    to={pathFor(id, lang)}
                    data-testid={`related-service-${id}`}
                    className="group bg-white rounded-2xl p-6 md:p-7 ring-1 ring-[#181B24]/10 hover-lift transition-all"
                  >
                    <h3 className="font-heading font-bold uppercase tracking-[0.02em] text-lg md:text-xl text-[#181B24] mb-2">
                      {sm.h1}
                    </h3>
                    <p className="font-body text-[#181B24]/70 text-sm md:text-base mb-4 line-clamp-2">
                      {sm.description}
                    </p>
                    <span className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.15em] text-xs text-[#181B24] group-hover:text-[#FFE21A] transition-colors">
                      {PAGE_COPY[lang].servicesIndex.readMore}
                      <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
